import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Stack,
  Chip,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AccountBalance as AccountBalanceIcon,
  Verified as VerifiedIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import client from '../../../API';

const PublicNOAView = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');

  const [noaData, setNoaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acknowledgeDialog, setAcknowledgeDialog] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');

  useEffect(() => {
    fetchNOA();
  }, [token, email]);

  const fetchNOA = async () => {
    if (!token || !email) {
      setError('Invalid access link. Please use the link from your email.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await client.noa.viewPublic(token, email);
      setNoaData(response);
      setAcknowledged(response.noa.status === 'acknowledged');
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load NOA. The link may be invalid or expired.');
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    if (confirmEmail.toLowerCase() !== email.toLowerCase()) {
      alert('Email confirmation does not match');
      return;
    }

    try {
      setAcknowledging(true);
      await client.noa.acknowledgePublic(token, email);
      setAcknowledged(true);
      setAcknowledgeDialog(false);
      fetchNOA(); // Refresh to show updated status
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to acknowledge NOA');
    } finally {
      setAcknowledging(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Container>
    );
  }

  const { noa, creator_name, debtor_name, can_acknowledge, remit_instructions } = noaData;

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <DescriptionIcon sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold' }}>
                Notice of Assignment
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Notice ID: {noa.id}
              </Typography>
            </Box>
          </Box>

          {acknowledged && (
            <Alert 
              severity="success" 
              icon={<VerifiedIcon />}
              sx={{ 
                mt: 2, 
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' }
              }}
            >
              <AlertTitle>Acknowledged</AlertTitle>
              This NOA has been acknowledged on {new Date(noa.noa_ack_at).toLocaleString()}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Assignment Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon color="primary" />
                Assignment Details
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Assignor (Vendor)</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{creator_name}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Assignee</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>Blitz Systems Corp.</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Debtor (Payor)</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{debtor_name}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                  <Chip 
                    label={noa.is_blanket ? "Blanket Assignment" : "Specific Invoice"}
                    color={noa.is_blanket ? "primary" : "default"}
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    icon={acknowledged ? <VerifiedIcon /> : <ScheduleIcon />}
                    label={acknowledged ? "Acknowledged" : "Pending Acknowledgment"}
                    color={acknowledged ? "success" : "warning"}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Remittance Instructions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceIcon color="primary" />
                Remittance Instructions
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Important</AlertTitle>
                All payments must be directed to the account below
              </Alert>

              <Stack spacing={1.5}>
                {remit_instructions.bank_name && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Bank Name</Typography>
                    <Typography variant="body1">{remit_instructions.bank_name}</Typography>
                  </Box>
                )}

                {remit_instructions.account_holder && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Account Holder</Typography>
                    <Typography variant="body1">{remit_instructions.account_holder}</Typography>
                  </Box>
                )}

                {remit_instructions.account_number && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Account Number</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {remit_instructions.account_number}
                    </Typography>
                  </Box>
                )}

                {remit_instructions.routing_number && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Routing Number</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {remit_instructions.routing_number}
                    </Typography>
                  </Box>
                )}

                {remit_instructions.swift_code && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">SWIFT Code</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {remit_instructions.swift_code}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Legal Notice */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Notice of Assignment & Direction to Pay
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    1. Assignment
                  </Typography>
                  <Typography variant="body2">
                    The Assignor hereby confirms that it has irrevocably assigned, transferred, and sold to Blitz all rights, 
                    title, and interest in and to the receivable(s) and all related proceeds.
                  </Typography>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    2. Direction to Pay
                  </Typography>
                  <Typography variant="body2">
                    Debtor is hereby directed to remit payment solely and directly to Blitz using the Remittance Instructions above. 
                    Payment to any party other than Blitz will not discharge Debtor's obligation.
                  </Typography>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    3. Continuing Notice {noa.is_blanket && '(Blanket)'}
                  </Typography>
                  <Typography variant="body2">
                    {noa.is_blanket 
                      ? "This Notice applies on a continuing basis to any present and future invoices issued via the Blitz platform by Assignor to Debtor, unless Debtor receives a written revocation from Blitz."
                      : "This Notice applies to the specific invoice referenced above."}
                  </Typography>
                </Paper>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Acknowledgment Section */}
        {can_acknowledge && !acknowledged && (
          <Grid item xs={12}>
            <Card sx={{ border: '2px solid #667eea' }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: '#667eea' }}>
                  Action Required: Acknowledge Receipt
                </Typography>
                <Typography variant="body1" paragraph>
                  By acknowledging this Notice of Assignment, you confirm that:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="• You have reviewed and understood the assignment notice" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Future payments will be directed to Blitz Systems Corp." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Your AP system will be updated with new remittance instructions" />
                  </ListItem>
                </List>
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => setAcknowledgeDialog(true)}
                    sx={{ 
                      px: 6, 
                      py: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}
                  >
                    Acknowledge NOA
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Acknowledgment Dialog */}
      <Dialog open={acknowledgeDialog} onClose={() => setAcknowledgeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Acknowledgment</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Please confirm your email address to acknowledge this Notice of Assignment:
          </Typography>
          <TextField
            label="Confirm Email Address"
            fullWidth
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder={email}
            helperText="Enter the email address this NOA was sent to"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcknowledgeDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAcknowledge}
            disabled={acknowledging || !confirmEmail}
            startIcon={acknowledging ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {acknowledging ? 'Processing...' : 'Confirm Acknowledgment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">
          Questions? Contact <a href="mailto:legal@useblitz.co">legal@useblitz.co</a>
        </Typography>
      </Box>
    </Container>
  );
};

export default PublicNOAView;